/**
 * Explore data hook (POS2-882) — react-query over the 881 pivot endpoint. Mirrors
 * the waste-stats hook family: direct `fetch`, `body.data` unwrap, params in a
 * pure builder. Fires two queries — the current window and the immediately
 * preceding one for the delta — with the prior call **sequenced after** the main
 * resolves (never concurrent: two window-length scans in flight starve the shared
 * micro-compute box — POS2-970/994, and the load-bearing interim guard until the
 * POS2-829 rollup). A measure switch never refetches: measure is not a server
 * param (all three measures ship in every cell), so the query key is identical.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import type { ExploreState, Measure, Split } from "@/hooks/use-explore-state";
import type { WasteFilters } from "@/hooks/use-waste-filters";
import type { ExploreWindow, Granularity } from "@/lib/explore-window";
import { computeDelta, precedingWindow, type ExploreResponse } from "@/lib/explore-engine";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export class ExploreError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ExploreError";
  }
}

/** A 503 is the statement-timeout guard (POS2-994/881): the window is too large
 * for the current instance (monthly 12-month pre-rollup) — a clean degrade, not
 * a bug. Views show "try a shorter window" instead of a generic error. */
export function isTooLargeError(error: unknown): boolean {
  return error instanceof ExploreError && error.status === 503;
}

/** Pivot request params. Enforces the 881 caller conventions: status=successful
 * always; packaging excluded unless a waste_type filter is active or splitting by
 * reason; measure is deliberately absent (client-side switch). */
export function buildExploreParams(
  window: ExploreWindow,
  gran: Granularity,
  split: Split,
  filters: WasteFilters,
  siteId: string,
  opts?: { uncapped?: boolean },
): URLSearchParams {
  const p = new URLSearchParams();
  p.set("date_from", window.from.toISOString());
  p.set("date_to", window.to.toISOString());
  p.set("granularity", gran);
  p.set("split", split);
  p.set("site_id", siteId);
  p.set("status", "successful");
  // Table view wants every split value (no top-N cap → no 'remainder' fold); charts stay capped.
  if (opts?.uncapped) p.set("uncapped", "true");
  else p.set("top_n", "8");
  if (filters.device_serial) p.set("device_serial", filters.device_serial);
  if (filters.service_time) p.set("service_time", filters.service_time);
  if (filters.waste_type) p.set("waste_type", filters.waste_type);
  if (filters.time_from) p.set("time_from", filters.time_from);
  if (filters.time_to) p.set("time_to", filters.time_to);
  // Reason must keep packaging visible as a slice; an explicit waste_type filter
  // already narrows and wins. Otherwise the food-only universe (POS2-861).
  if (!filters.waste_type && split !== "reason") p.set("exclude_waste_type", "packaging");
  return p;
}

async function fetchExplore(
  params: URLSearchParams,
  headers: Record<string, string>,
): Promise<ExploreResponse> {
  const res = await fetch(`${API_URL}/api/v1/waste/events/explore?${params.toString()}`, {
    headers,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ExploreError(`Failed to load explore data: ${res.status} ${text}`, res.status);
  }
  const body = (await res.json()) as { data: ExploreResponse };
  return body.data;
}

export type ExploreData = {
  data: ExploreResponse | undefined;
  /** The preceding window's pivot — its last bucket seeds the Table's first-row Δ (POS2-1038). */
  priorData: ExploreResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isTooLarge: boolean;
  /** Signed change vs the preceding window, per measure (null = hide the pill). */
  delta: Record<Measure, number | null>;
};

export function useExploreData(
  state: ExploreState,
  filters: WasteFilters,
  siteId: string | null,
): ExploreData {
  const headers = useApiHeaders();
  const hasAuth = !!headers.Authorization.replace("Bearer ", "");
  const canQuery = hasAuth && !!siteId;

  // The Table view reads every split column (uncapped); the charts read the capped 8+remainder
  // pivot. One view-driven main query — no second concurrent scan.
  const mainParams = buildExploreParams(
    state.window,
    state.gran,
    state.split,
    filters,
    siteId ?? "",
    {
      uncapped: state.view === "table",
    },
  );
  const mainQuery = useQuery<ExploreResponse>({
    queryKey: ["explore", mainParams.toString()],
    queryFn: () => fetchExplore(mainParams, headers),
    enabled: canQuery,
    placeholderData: (prev) => prev,
  });

  // Same cap as the main query so the preceding window's columns line up with the table's: uncapped
  // for the Table (every column gets a first-row baseline), capped for the charts. The panel delta
  // only reads its totals, which are cap-independent. Still sequenced after main — no extra scan.
  const priorParams = buildExploreParams(
    precedingWindow(state.window),
    state.gran,
    state.split,
    filters,
    siteId ?? "",
    { uncapped: state.view === "table" },
  );
  const priorQuery = useQuery<ExploreResponse>({
    queryKey: ["explore", priorParams.toString()],
    queryFn: () => fetchExplore(priorParams, headers),
    // Sequenced: never fire the second scan until the main succeeds (a main that
    // 503s must not trigger it). isSuccess, not isFetched.
    enabled: canQuery && mainQuery.isSuccess,
    placeholderData: (prev) => prev,
  });

  const delta = useMemo<Record<Measure, number | null>>(() => {
    const cur = mainQuery.data;
    const prior = priorQuery.data;
    if (!cur) return { gbp: null, kg: null, caps: null };
    return {
      gbp: computeDelta(cur.total_cost, prior?.total_cost),
      kg: computeDelta(cur.total_weight, prior?.total_weight),
      caps: computeDelta(cur.total_events, prior?.total_events),
    };
  }, [mainQuery.data, priorQuery.data]);

  return {
    data: mainQuery.data,
    priorData: priorQuery.data,
    isLoading: mainQuery.isLoading,
    isError: mainQuery.isError,
    error: mainQuery.error,
    isTooLarge: isTooLargeError(mainQuery.error),
    delta,
  };
}
